<template>
  <div class="HasHealthInsuranceView">
    <h2 class="gl_form-title">
      {{ legend }}
    </h2>
    <CardInfo :icon="hint.icon">{{ hint.label }}</CardInfo>
    <RadioButtonGroup
      :value="hasHealthInsurance"
      :model="hasHealthInsurance"
      id="has-health-insurance"
      :options="options"
      flex-direction="row"
      :rules="[validationRules.radioRequired]"
      @update:modelValue="(newValue) => (hasHealthInsurance = newValue)"
    />
  </div>
</template>
<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRules } from '@/modules/core/composables/useRulesComposable'

import CardInfo from '@/modules/common/common/components/panels/CardInfoComponent.vue'
import RadioButtonGroup from '@/modules/core/components/ui/RadioButtonGroupComponent.vue'

import type { OptionsValuesInterface } from '@/modules/core/models/types'
import type Insured from '@/modules/common/common/models/APP/Insured'
import DentalRaterAnswers from '@/modules/dental/rater/models/APP/DentalRaterAnswers'
import { ForWhomEnum } from '@/modules/core/models/enums'
import DentalConfigurations from '@/modules/dental/common/models/APP/DentalConfigurations'

const { t } = useI18n()
const { required } = useRules()

defineExpose({ triggerSubmit })

const props = defineProps({
  nextForm: {
    type: Array<String>,
    required: true
  },
  answers: {
    type: DentalRaterAnswers,
    default: () => {}
  },
  configurations: {
    type: DentalConfigurations,
    required: true
  }
})

const hasHealthInsurance = ref(props.answers.hasHealthInsurance)

const legend = computed(() => {
  if (props.answers.forWhom === ForWhomEnum.ME) {
    return props.configurations.tags.hasHealthInsuranceMeTitle
  }
  if (props.answers.forWhom === ForWhomEnum.ME_MORE) {
    return props.configurations.tags.hasHealthInsuranceMeAndOthersTitle
  }
  if (props.answers.insureds.length > 1) {
    return props.configurations.tags.hasHealthInsuranceOthersTitle
  }
  return props.configurations.tags.hasHealthInsuranceOtherTitle
})

const hint = computed(() => {
  if (props.answers.forWhom === ForWhomEnum.ME) {
    return props.configurations.tags.hasHealthInsuranceMeAlert
  }
  if (props.answers.forWhom === ForWhomEnum.ME_MORE) {
    return props.configurations.tags.hasHealthInsuranceMeAndOthersAlert
  }
  if (props.answers.insureds.length > 1) {
    return props.configurations.tags.hasHealthInsuranceOthersAlert
  }
  return props.configurations.tags.hasHealthInsuranceOtherAlert
})

const validationRules = {
  radioRequired: (value: string) => required(value, 'common.validations.option')
}

const options: OptionsValuesInterface[] = [
  {
    label: t('common.choices.yes'),
    value: 'yes'
  },
  {
    label: t('common.choices.no'),
    value: 'no'
  }
]

function triggerSubmit() {
  const page = props.nextForm[0]
  const answers = {
    insureds: props.answers.insureds.map((i: Insured) => {
      i.hasHealthPolicy = hasHealthInsurance.value
      return i
    }),
    hasHealthInsurance: hasHealthInsurance.value
  }

  return { page, answers }
}
</script>
